import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { ButtonBase, Typography } from '@mui/material';
import { useNewsLetter } from '@/data/Content/NewsLetter';
import Link from 'next/link';

const InputFrame = styled('div')`
	display: flex;
	width: fit-content;
	min-width: 100%;
	max-width: 100%;
	flex-direction: row;
	border-radius: 50px;
	${({ theme }) => theme.breakpoints.up('md')} {
		min-width: 300px;
		max-width: 367px;
	}
	${({ theme }) => theme.breakpoints.down('sm')} {
		width: 100%;
	}
`;

const Input = styled('input')(({ theme }) => ({
	flex: 1,
	[theme.breakpoints.up('md')]: {
		maxWidth: 200,
	},
	borderTopLeftRadius: 50,
	borderBottomLeftRadius: 50,
	borderWidth: 1,
	borderStyle: 'solid',
	borderColor: theme.palette.grey[400],
	padding: '0.5rem 1rem',
	fontSize: 'var(--font-size-caption)',
	[theme.breakpoints.down('sm')]: {
		padding: '0.875rem 1rem',
	},
	'&:-internal-autofill-selected, &:-webkit-autofill': {
		appearance: 'none',
		backgroundImage: 'none !important',
		backgroundColor: 'transparent !important',
		color: 'inherit !important',
		'-webkit-text-fill-color': 'inherit !important',
		transition: 'background-color 5000s ease-in-out 0s',
	},
	outline: 'none',
	'&:focus': {
		borderColor: theme.palette.grey[400],
	},
}));

const Button = styled('button')(({ theme }) => ({
	backgroundColor: theme.palette.primary.main,
	color: theme.palette.primary.contrastText,
	padding: '0.5rem 1rem',
	borderWidth: 0,
	borderTopRightRadius: 50,
	borderBottomRightRadius: 50,
	fontSize: 'var(--font-size-small)',
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: theme.palette.primary.dark,
	},
	[theme.breakpoints.down('sm')]: {
		padding: '0rem 1rem',
	},
}));

const HelperText = styled(Typography)<{
	showError: boolean;
}>(({ theme, showError }) => ({
	color: showError ? theme.palette.error.main : theme.palette.grey[400],
	fontSize: '0.70rem',
	mt: 0.2,
}));

const PrivacyLink = styled(Link)({
	color: '#929292',
	textDecoration: 'none',
	'&:hover': {
		color: 'gray',
	},
});

export const SubscriptionNewsLetter = ({
	placeholder,
	buttonLabel,
}: {
	placeholder: string;
	buttonLabel: string;
}) => {
	const { emailNewsLetter, mutateNewsLetter, handleEmailChange, error, success } = useNewsLetter();

	const helperText = useMemo(() => {
		const privacyLink = (
			<PrivacyLink
				sx={{
					'&:hover': {
						borderBottom: '1px solid #929292',
					},
				}}
				href="/aviso-privacidad"
			>
				{'Política de Privacidad'}
			</PrivacyLink>
		);

		if (error) return error;
		if (success) {
			return (
				<>
					{'Al registrarte, aceptas nuestra '}
					{privacyLink}
				</>
			);
		}
		return (
			<>
				{'Al registrarte, aceptas nuestra '}
				{privacyLink}
			</>
		);
	}, [error, success]);

	return (
		<>
			<InputFrame>
				<Input
					type="email"
					id="mce-EMAIL"
					value={emailNewsLetter}
					placeholder={placeholder}
					maxLength={100}
					sx={{
						height: { xs: '48px', lg: '39px' },
						width: '200px',
						fontSize: { sm: 'var(--font-size-caption)', xs: 'var(--font-size-body)' },
						fontFamily: 'Open Sans',
					}}
					required
					onChange={handleEmailChange}
				/>
				<ButtonBase
					sx={{
						width: { xs: '150px', md: '106px' },
						height: { xs: '48px', lg: '39px' },
						fontSize: { sm: 'var(--font-size-small)', xs: 'var(--font-size-default)' },
						fontFamily: 'Open Sans',
						backgroundColor: 'button.primary',
						color: '#FBFBFB',
						borderTopRightRadius: 50,
						borderBottomRightRadius: 50,
						borderWidth: 0,
						cursor: 'pointer',
						'&:hover': {
							backgroundColor: 'button.primary.dark',
						},
					}}
					id="mc-embedded-subscribe"
					onClick={mutateNewsLetter}
				>
					{buttonLabel}
				</ButtonBase>
			</InputFrame>
			<HelperText
				showError={!!error}
				sx={{
					marginTop: '6px',
					textAlign: 'left',
					fontSize: 'var(--font-size-caption)',
					fontFamily: 'Open Sans',
					color: error ? '#AB2326' : '#929292',
				}}
			>
				{helperText}
			</HelperText>
		</>
	);
};
